// WyngThemeProvider — owns the visual identity (Obsidian Gold / Neon Rose).
//
// Source of truth: `users.theme` in MongoDB. On mount we pull from
// /auth/me; switching calls /settings/theme PUT and updates the
// `data-theme` attribute on <html>, which our CSS variables key off.
//
// We deliberately separate this from `next-themes` (which controls
// light/dark mode) so the two concerns don't collide.

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/api";

const ThemeContext = createContext({
    theme: "obsidian_gold",
    setTheme: () => {},
    ready: false,
});

export const THEMES = {
    obsidian_gold: {
        id: "obsidian_gold",
        name: "Obsidian Gold",
        tagline: "Krachtig. Focused. High performance.",
        accent: "#D4AF37",
        accentBright: "#FFD56B",
        glow: "rgba(255, 213, 107, 0.45)",
        gradient: "linear-gradient(135deg, #0B0B0D 0%, #D4AF37 100%)",
        builtin: true,
    },
    neon_rose: {
        id: "neon_rose",
        name: "Neon Rose",
        tagline: "Empowered. Balanced. Unstoppable.",
        accent: "#FF2D6E",
        accentBright: "#FF7DB8",
        glow: "rgba(255, 125, 184, 0.55)",
        gradient: "linear-gradient(135deg, #0B0B0D 0%, #FF2D6E 100%)",
        builtin: true,
    },
    pearl_white: {
        id: "pearl_white",
        name: "Pearl White",
        tagline: "Daylight clarity. Soft luxe.",
        accent: "#E8E1D5",
        accentBright: "#FFFCF6",
        glow: "rgba(255, 252, 246, 0.55)",
        gradient: "linear-gradient(135deg, #1A1A1F 0%, #E8E1D5 100%)",
        builtin: false,
    },
    midnight_eclipse: {
        id: "midnight_eclipse",
        name: "Midnight Eclipse",
        tagline: "Deep sky. Silver pulse.",
        accent: "#9B8CFF",
        accentBright: "#C7BCFF",
        glow: "rgba(199, 188, 255, 0.55)",
        gradient: "linear-gradient(135deg, #0B0B0D 0%, #9B8CFF 100%)",
        builtin: false,
    },
    emerald_fox: {
        id: "emerald_fox",
        name: "Emerald Fox",
        tagline: "Forest depth. Predator focus.",
        accent: "#10B981",
        accentBright: "#6EE7B7",
        glow: "rgba(110, 231, 183, 0.55)",
        gradient: "linear-gradient(135deg, #0B0B0D 0%, #10B981 100%)",
        builtin: false,
    },
    celestial: {
        id: "celestial",
        name: "Celestial",
        tagline: "Reserved for Legend tier — only seen by the few.",
        accent: "#F0E6FF",
        accentBright: "#FFFFFF",
        glow: "rgba(240, 230, 255, 0.55)",
        gradient: "linear-gradient(135deg, #050010 0%, #F0E6FF 100%)",
        builtin: false,
    },
};

function applyToDom(theme) {
    const html = document.documentElement;
    if (!html) return;
    html.setAttribute("data-theme", theme);
}

export function WyngThemeProvider({ children }) {
    const [theme, setThemeState] = useState("obsidian_gold");
    const [ready, setReady] = useState(false);

    // Initial load: read theme from /auth/me. We tolerate a missing token
    // (pre-login users see the default). If the user has no theme set yet
    // we keep the default — onboarding will write one on completion.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/auth/me");
                if (cancelled) return;
                const t = r.data?.theme || "obsidian_gold";
                setThemeState(t);
                applyToDom(t);
            } catch {
                applyToDom("obsidian_gold");
            } finally {
                if (!cancelled) setReady(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Apply on any external change too (e.g. login switches user).
    useEffect(() => { applyToDom(theme); }, [theme]);

    const setTheme = useCallback(async (next) => {
        if (!THEMES[next]) return;
        // Optimistic — apply locally first so the cross-fade feels instant.
        setThemeState(next);
        applyToDom(next);
        try {
            await api.put("/settings/theme", { theme: next });
        } catch {
            /* Persistence failure is non-fatal — the user's visual choice
               stays locally for the session. Next /auth/me will reconcile. */
        }
    }, []);

    const value = useMemo(() => ({
        theme,
        setTheme,
        ready,
        config: THEMES[theme] || THEMES.obsidian_gold,
    }), [theme, setTheme, ready]);

    return (
        <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    );
}

export function useWyngTheme() {
    return useContext(ThemeContext);
}
